import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { 
  Mic, 
  Loader2, 
  Search,
  Activity,
  Sparkles
} from 'lucide-react';

/**
 * EvolveAnalysisCard - Evolve AI score regeneration tools for admins
 * Handles batch analysis of sessions missing scores
 */
export const EvolveAnalysisCard = ({ api }) => {
  const [evolveAnalysisRunning, setEvolveAnalysisRunning] = useState(false);
  const [evolveAnalysisResult, setEvolveAnalysisResult] = useState(null);

  const runEvolveAnalysis = async (dryRun = true) => {
    setEvolveAnalysisRunning(true);
    setEvolveAnalysisResult(null);
    try {
      const response = await api.post(`/evolve/admin/analyze-all-sessions?dry_run=${dryRun}`);
      setEvolveAnalysisResult(response.data);
      if (dryRun) {
        if (response.data.sessions_needing_analysis > 0) {
          toast.info(`Found ${response.data.sessions_needing_analysis} sessions needing analysis. Click "Analyze Now" to generate scores.`);
        } else {
          toast.success('All sessions already have scores. No analysis needed.');
        }
      } else {
        toast.success(`Analyzed ${response.data.sessions_analyzed} sessions! ${response.data.sessions_failed} failed.`);
      }
    } catch (error) {
      toast.error('Failed to run Evolve analysis');
      console.error(error);
    } finally {
      setEvolveAnalysisRunning(false);
    }
  };

  return (
    <Card className="border-2 border-violet-500/20 bg-gradient-to-r from-violet-500/5 to-purple-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mic className="h-5 w-5 text-violet-500" />
          Evolve AI Score Generation
        </CardTitle>
        <CardDescription>
          Generate AI scores for Evolve presentation sessions that show 0 scores
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 p-4 rounded-lg border bg-background">
          <div>
            <Label className="text-base font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-violet-500" />
              Analyze Sessions Without Scores
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Scans all Evolve sessions and generates AI feedback/scores for those missing scores.
              This fixes sessions showing Score: 0 in the admin report.
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={() => runEvolveAnalysis(true)}
              disabled={evolveAnalysisRunning}
              variant="outline"
              className="flex-1"
              data-testid="evolve-check-btn"
            >
              {evolveAnalysisRunning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Check Sessions
            </Button>
            <Button 
              onClick={() => runEvolveAnalysis(false)}
              disabled={evolveAnalysisRunning || !evolveAnalysisResult || evolveAnalysisResult.sessions_needing_analysis === 0}
              className="flex-1 bg-violet-600 hover:bg-violet-700"
              data-testid="evolve-analyze-btn"
            >
              {evolveAnalysisRunning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Analyze Now
            </Button>
          </div>
          
          {evolveAnalysisResult && (
            <div className={`p-3 rounded-lg text-sm ${
              evolveAnalysisResult.sessions_needing_analysis > 0 
                ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200' 
                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
            }`}>
              {evolveAnalysisResult.dry_run ? (
                evolveAnalysisResult.sessions_needing_analysis > 0 ? (
                  <p>
                    <strong>{evolveAnalysisResult.sessions_needing_analysis}</strong> sessions out of <strong>{evolveAnalysisResult.sessions_checked}</strong> need analysis. Click Analyze Now to generate scores.
                  </p>
                ) : (
                  <p>All {evolveAnalysisResult.sessions_checked} sessions already have scores. No analysis needed.</p>
                )
              ) : (
                <div>
                  <p>Analyzed <strong>{evolveAnalysisResult.sessions_analyzed}</strong> sessions successfully.</p>
                  {evolveAnalysisResult.sessions_failed > 0 && (
                    <p className="text-amber-600 dark:text-amber-400 mt-1">
                      {evolveAnalysisResult.sessions_failed} sessions failed (may have insufficient data).
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EvolveAnalysisCard;
